// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type PelliRobsonGraphType = {
  examData: ExamSectionReportType;
};

const PelliRobsonGraph = ({ examData }: PelliRobsonGraphType) => {
  const { t } = useTranslation();

  const displayedLetters = useMemo(() => {
    const letters = examData?.state?.letters ?? [];

    return letters?.flatMap((letter) =>
      letter?.letters?.map((char) => ({
        color:
          char?.State === "GuessedCorrectly"
            ? "#13A321"
            : char?.State === "Skipped"
              ? "#202338"
              : char?.State === "None"
                ? "#202338"
                : "#B21515",
        backgroundImage:
          char?.State === "Skipped"
            ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
            : "none",
        backgroundSize: "8px 8px",
        value: char?.Value,
      }))
    );
  }, [examData?.state?.letters]);

  const isError = useMemo(
    () => !examData?.state?.letters || !examData?.state?.letters?.length,
    [examData?.state?.letters]
  );

  return (
    <Grid
      sx={{
        display: "grid",
        gridTemplateColumns: isError ? "repeat(1, 1fr)" : "repeat(6, 1fr)",
        gridGap: { xs: "5px", sm: "10px" },
        maxWidth: { xs: "300px", sm: "450px" },
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {isError ? (
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("Error: No patient responses recorded.")}
        </Typography>
      ) : (
        displayedLetters?.map((letter, index) => {
          return (
            <Grid
              item
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundImage: letter?.backgroundImage,
                backgroundSize: letter?.backgroundSize,
                height: "32px",
                width: "32px",

                marginRight: {
                  xs: index === 2 ? "30px" : "0px",
                  sm: index === 2 ? "60px" : "0px",
                },
              }}
            >
              <Typography color={letter.color} fontSize="20px">
                {letter.value}
              </Typography>
            </Grid>
          );
        })
      )}
    </Grid>
  );
};

export default PelliRobsonGraph;
